import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { postAccessCode } from 'src/app/CspUserDetail/redux/cspUserDetail.actions'
import {
  selectCspUser,
  selectHasCreatedAccessCodeFromState,
  selectPostingAccessCodeFromState,
  selectUser,
} from 'src/app/CspUserDetail/redux/cspUserDetail.selectors'
import AccessCodesTooltip from 'src/components/AccessCodesTooltip/AccessCodesTooltip'
import Collapsible from 'src/components/Collapsible/Collapsible'
import CreditCardCapTooltip from 'src/components/CreditCardCapTooltip/CreditCardCapTooltip'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LeftFlyout, {
  LeftFlyoutBodyWidths,
} from 'src/components/LeftFlyout/LeftFlyout'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RelatedCompaniesTooltip from 'src/components/RelatedCompaniesTooltip/RelatedCompaniesTooltip'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import TextWithTooltipOnClick from 'src/components/TextWithTooltipOnClick/TextWithTooltipOnClick'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import VirtualCardCapTooltip from 'src/components/VirtualCardCapTooltip/VirtualCardCapTooltip'
import { AvatarSizes } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { AccessCodeResourceType } from 'src/models/accessCodes.models'
import { getCspUser } from 'src/redux/actions/data/users.actions'
import { css } from 'styled-components'

const labelWithTooltip = css`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

const firstChildMargins = css`
  display: flex;
  flex-direction: column;

  .text-with-tooltip-on-click__text {
    font-weight: bold;
  }

  & > :first-child {
    margin-top: 0;
    margin-bottom: 10px;
  }
`

const disabled = css`
  margin-left: auto;
  pointer-events: none;
`

const enabled = css`
  margin-left: auto;
  pointer-events: auto;
`
const valueStack = css`
  display: flex;
  flex-direction: column;

  .text-with-tooltip-on-click__text {
    font-weight: bold;
  }
`
const statCss = css`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`
const statLabelCss = css`
  ${getFontCss(
    12,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.medium,
  )};
`
const statValueCss = css`
  ${getFontCss(
    12,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  margin-left: 16px;
  text-align: right;
  margin-top: auto;
`

const CspUserDetail: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const user = useSelector(selectUser)
  const cspUser = useSelector(selectCspUser)
  const postingAccessCode = useSelector(selectPostingAccessCodeFromState)
  const hasCreatedAccessCode = useSelector(selectHasCreatedAccessCodeFromState)

  const dispatch = useFlyoutDispatch()

  const [isMoreStatsOpen, setIsMoreStatsOpen] = useState(false)
  const [disableCreateButton, setDisableCreateButton] = useState(false)

  useEffect((): void => {
    if (user?.id) {
      dispatch(getCspUser(user.id))
    }
  }, [user?.id])

  useEffect((): void => {
    if (postingAccessCode && !disableCreateButton) {
      setDisableCreateButton(true)
      setTimeout(() => {
        setDisableCreateButton(false)
      }, 10000)
    }
  }, [postingAccessCode])

  const onHandleGenerateNewCode = (): void => {
    if (user?.id) {
      dispatch(
        postAccessCode(user.id, {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          type: AccessCodeResourceType.creditCard,
          volume: 1,
        }),
      )
    }
  }

  const baseStats =
    cspUser !== undefined
      ? [
          {
            label: translate('Last login'),
            value: cspUser.formattedLastLogin,
          },
          {
            label: translate('Roles'),
            value: cspUser.roles.map((role, k) =>
              k !== cspUser.roles.length - 1
                ? `${role.slice(0, 1)}${role.slice(1).replace(/_/g, ' ').toLowerCase()}, `
                : `${role.slice(0, 1)}${role.slice(1).replace(/_/g, ' ').toLowerCase()}`,
            ),
          },
          {
            label: (
              <div css={labelWithTooltip}>
                {translate('Related Companies')}
                <RelatedCompaniesTooltip
                  openLeft={false}
                ></RelatedCompaniesTooltip>
              </div>
            ),
            value: cspUser.companies.map((company, k) =>
              k !== cspUser.companies.length - 1 ? `${company}, ` : company,
            ),
          },
        ]
      : []

  const hiddenStats =
    cspUser !== undefined
      ? [
          {
            label: (
              <div css={labelWithTooltip}>
                {translate('Credit Cards Cap')}
                <CreditCardCapTooltip openLeft={false}></CreditCardCapTooltip>
              </div>
            ),
            value: `${cspUser.creditCardCap.current}/${cspUser.creditCardCap.max}`,
          },
          {
            label: (
              <div css={labelWithTooltip}>
                {translate('Virtual Cards Cap')}
                <VirtualCardCapTooltip openLeft={false}></VirtualCardCapTooltip>
              </div>
            ),
            value: (
              <div css={valueStack}>
                {cspUser.virtualCardCap.map((cap, i) => (
                  <span key={i}>
                    {translate('${capAmount} on ${lastFour}', {
                      capAmount: `${cap.current}/${cap.max}`,
                      lastFour: `(${cap.last4})`,
                    })}
                  </span>
                ))}
              </div>
            ),
          },
          {
            label: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: start;
                `}
              >
                <div css={firstChildMargins}>
                  <div css={labelWithTooltip}>
                    {translate('Access Codes')}
                    <AccessCodesTooltip openLeft={false}></AccessCodesTooltip>
                  </div>
                  {cspUser.accessCodes.slice(0, 5).map((code, i) => (
                    <div key={i}>
                      {code.resourceId !== undefined && (
                        <Fragment>{translate('(used)')} </Fragment>
                      )}
                      <span
                        css={css`
                          text-decoration: ${code.resourceId !== undefined
                            ? 'line-through'
                            : 'none'};
                        `}
                      >
                        {code.code}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ),
            value: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: end;
                `}
              >
                <div css={firstChildMargins}>
                  <TextWithTooltipOnClick
                    css={
                      postingAccessCode || disableCreateButton
                        ? disabled
                        : enabled
                    }
                    onClick={onHandleGenerateNewCode}
                    delayTrigger
                    underline
                    triggerNow={hasCreatedAccessCode}
                    text={translate('Generate new code')}
                    tooltipBody={translate('New code generated!')}
                  />
                  {cspUser.accessCodes.slice(0, 5).map((code, i) => (
                    <span key={i}>
                      {' '}
                      {moment(code.createdAt).isSame(moment(), 'day')
                        ? translate('Today')
                        : `Created at ${moment(code.createdAt).format('MM/DD/YYYY')}`}
                    </span>
                  ))}
                </div>
              </div>
            ),
          },
        ]
      : []

  return user === undefined ? (
    <NotFoundRightFlyoutContent />
  ) : (
    <LeftFlyout
      css={css`
        overflow: auto;
      `}
      width={LeftFlyoutBodyWidths.CspUserDetail}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <UserAvatar user={user} size={AvatarSizes.ExtraHuge} />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            margin-left: 16px;

            h2 {
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 12px;
            }

            span {
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};

              &:nth-child(n + 3) {
                margin-top: 4px;
              }
            }
          `}
        >
          <h2>{user.fullNameOrEmail}</h2>
          {user.hasFullName && <span>{user.email}</span>}
        </div>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 40px;

          h3 {
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          }
        `}
      >
        <h3>{translate('User stats')}</h3>
        {baseStats.map((stat, i) => (
          <div key={i} css={statCss}>
            <span css={statLabelCss}>{stat.label}</span>
            <span css={statValueCss}>{stat.value}</span>
          </div>
        ))}
        <Collapsible isOpen={isMoreStatsOpen}>
          {hiddenStats.map((stat, i) => (
            <div key={i} css={statCss}>
              <span css={statLabelCss}>{stat.label}</span>
              <span css={statValueCss}>{stat.value}</span>
            </div>
          ))}
        </Collapsible>
        <HoverableLink
          css={css`
            display: flex;
            align-items: center;
            margin-top: 16px;

            svg {
              transform: scale(calc(12 / 24));
            }
          `}
          size={HoverableLinkSizes.Small}
          onClick={(): void => {
            setIsMoreStatsOpen((val) => !val)
          }}
          ariaLabelSource={isMoreStatsOpen ? 'Show less' : 'Show more stats'}
        >
          {isMoreStatsOpen && (
            <>
              {translate('Show less')}
              <SvgChevronUp />
            </>
          )}
          {!isMoreStatsOpen && (
            <>
              {translate('Show more stats')}
              <SvgChevronDown />
            </>
          )}
        </HoverableLink>
      </div>
    </LeftFlyout>
  )
}

export default CspUserDetail
